// src/pages/ForfeoFinds.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { supabase } from "../supabaseClient";
import { DealsMap } from "../components/DealsMap";
import {
  FaBolt,
  FaSearch,
  FaFire,
  FaClock,
  FaHeart,
  FaRegHeart,
  FaStar,
  FaShareAlt,
  FaSpinner,
  FaBell,
  FaTimes
} from "react-icons/fa";

// -----------------------------
// ENV / FEATURE FLAGS
// -----------------------------
const ENABLE_FINDS = (import.meta as any).env?.VITE_ENABLE_FINDS !== "false";
const API_URL = (import.meta as any).env?.VITE_API_URL || "";
const FINDS_ENDPOINT = `${API_URL}`.replace(/\/$/, "") + "/finds";

// -----------------------------
// TYPES
// -----------------------------
type FindType = "hotel" | "restaurant" | "spa" | "activity" | "bundle" | "last_call" | "other";
type FindSource = "finds" | "last_call";
type BadgeTone = "danger" | "warning" | "dark" | "neutral" | "gold" | "blue" | "emerald";

type FindDeal = {
  id: string;
  source: FindSource;
  source_id: string;
  title: string;
  description?: string | null;
  original_price?: number | null;
  price?: number | null;
  currency?: string | null;
  quantity_available?: number | null;
  expires_at?: string | null;
  image_url?: string | null;
  region?: string | null;
  type?: FindType | string | null;
  is_mystery?: boolean | null;
  reveal_after_purchase?: boolean | null;
  public_url?: string | null;
  badge?: string | null;
  badge_secondary?: string | null;
  sort_score?: number | null;
  collection_slugs?: string[] | null;
  finds_badge?: string | null;
  finds_badge_tone?: string | null;
  editorial_note?: string | null;
  best_for?: string | null;
  combo_tip?: string | null;
  teaser_title?: string | null;
  teaser_subtitle?: string | null;
  is_featured?: boolean | null;
  is_trending?: boolean | null;
  sponsored?: boolean | null;
  sponsor_priority?: number | null;
  manual_score_override?: number | null;
  tags?: string[] | null;
  lat?: number | null;
  lng?: number | null;
};

type FindCollection = {
  id: string;
  slug: string;
  title: string;
  subtitle?: string | null;
  sort_order: number;
  itemKeys: string[];
  origin?: "table" | "view";
};

type SortKey = "hot" | "price_asc" | "price_desc" | "expires";

// -----------------------------
// HELPERS
// -----------------------------
const normalize = (s: string) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
function safeNumber(n: any, fallback = 0): number { const v = typeof n === "number" ? n : Number(n); return Number.isFinite(v) ? v : fallback; }
function coerceStringArray(value: any): string[] | null {
  if (Array.isArray(value)) { const arr = value.map((x) => String(x ?? "").trim()).filter(Boolean); return arr.length ? arr : null; }
  if (typeof value === "string" && value.trim()) { const arr = value.split(",").map((x) => x.trim()).filter(Boolean); return arr.length ? arr : null; }
  return null;
}
function formatMoney(amount: number | null | undefined, currency = "CAD") {
  if (amount === null || amount === undefined) return "";
  const v = safeNumber(amount, 0);
  try { return new Intl.NumberFormat("fr-CA", { style: "currency", currency, maximumFractionDigits: 0 }).format(v); } 
  catch { return `${v} ${currency}`; }
}
function hoursLeft(expiresAt?: string | null): number | null {
  if (!expiresAt) return null;
  const t = new Date(expiresAt).getTime();
  if (!Number.isFinite(t)) return null;
  const diff = t - Date.now();
  if (diff <= 0) return 0;
  return Math.ceil(diff / (1000 * 60 * 60));
}
function clampText(s: string, max = 140) { const str = (s || "").trim(); if (str.length <= max) return str; return str.slice(0, max - 1).trimEnd() + "…"; }
function computeDiscountPct(original?: number | null, price?: number | null) {
  if (!original || !price || original <= 0 || price <= 0 || price >= original) return null;
  return Math.round(((original - price) / original) * 100);
}
function toneClass(tone: BadgeTone) {
  switch (tone) {
    case "danger": return "bg-red-600 text-white";
    case "warning": return "bg-amber-500 text-white";
    case "dark": return "bg-slate-900 text-white";
    case "gold": return "bg-amber-100 text-amber-900";
    case "blue": return "bg-blue-100 text-blue-900";
    case "emerald": return "bg-emerald-100 text-emerald-900";
    default: return "bg-slate-100 text-slate-900";
  }
}
function badgeForDeal(d: FindDeal) {
  const q = d.quantity_available ?? null;
  const h = hoursLeft(d.expires_at);
  if (d.source === "last_call") return { text: "⚡ Last Call", tone: "danger" as const };
  if (d.is_featured) return { text: "⭐ Choix Forfeo", tone: "gold" as const };
  if (d.is_trending) return { text: "🔥 Tendance", tone: "warning" as const };
  if (d.is_mystery || d.reveal_after_purchase) return { text: "🎭 Mystère", tone: "dark" as const };
  if (q !== null && q <= 3 && q > 0) return { text: "🔥 Quantité limitée", tone: "danger" as const };
  if (q !== null && q <= 0) return { text: "Épuisé", tone: "dark" as const };
  if (h !== null && h <= 24) return { text: "⏳ Expire bientôt", tone: "warning" as const };
  return { text: "✨ Trouvaille", tone: "neutral" as const };
}
function makeMergeKey(x: Partial<FindDeal>) {
  const src = (x.source || "finds") as FindSource;
  const sid = x.source_id || x.id || "";
  return `${src}:${sid}`;
}
function toneFromOverride(value?: string | null): BadgeTone {
  const v = (value || "").toLowerCase();
  if (["danger", "red"].includes(v)) return "danger";
  if (["warning", "amber", "orange"].includes(v)) return "warning";
  if (["dark", "black", "mystery"].includes(v)) return "dark";
  if (["gold", "premium", "signature"].includes(v)) return "gold";
  if (["blue"].includes(v)) return "blue";
  if (["emerald", "green"].includes(v)) return "emerald";
  return "neutral";
}
function computeFindsScoreLocal(d: FindDeal) {
  if (d.manual_score_override != null && Number.isFinite(Number(d.manual_score_override))) return Number(d.manual_score_override);
  const viewScore = d.sort_score != null && Number.isFinite(Number(d.sort_score)) ? Number(d.sort_score) : null;
  if (viewScore !== null) return viewScore;
  const disc = computeDiscountPct(d.original_price, d.price) ?? 0;
  const qty = d.quantity_available != null ? safeNumber(d.quantity_available, 999) : null;
  const h = hoursLeft(d.expires_at);
  let score = Math.min(30, disc * 0.6);
  if (qty != null) { if (qty <= 2) score += 15; else if (qty <= 5) score += 10; else if (qty <= 10) score += 5; }
  if (h != null) { if (h <= 6) score += 12; else if (h <= 24) score += 8; else if (h <= 72) score += 4; }
  if (d.source === "last_call") score += 4;
  if (d.is_trending) score += 10;
  if (d.is_featured) score += 10;
  if (d.sponsored) score += 3;
  return Math.round(score);
}
function humanizeCollectionSlug(slug: string) {
  const s = String(slug || "").trim();
  if (!s) return "Collection";
  const map: Record<string, string> = {
    hot_now: "Hot Now", last_call: "Last Call", under_100: "Moins de 100$", romantic: "Romantique",
    family: "Famille", wellness: "Détente", foodie: "Gourmand", premium: "Premium", weekend: "Week-end", outdoors: "Plein air", date_night: "Date night", top_picks: "Top Picks",
  };
  if (map[s]) return map[s];
  return s.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
}
function buildCollectionsFromItems(items: FindDeal[]): FindCollection[] {
  const bucket = new Map<string, { title: string; subtitle: string | null; sort_order: number; itemKeys: string[] }>();
  const defaultOrder = (slug: string) => {
    const order: Record<string, number> = { hot_now: 1, top_picks: 2, last_call: 3, under_100: 4, romantic: 5, family: 6, wellness: 7, foodie: 8, premium: 9, weekend: 10, outdoors: 11 };
    return order[slug] ?? 99;
  };
  for (const d of items) {
    const slugs = coerceStringArray(d.collection_slugs) || [];
    for (const slug of slugs) {
      if (!bucket.has(slug)) bucket.set(slug, { title: humanizeCollectionSlug(slug), subtitle: "Collection générée", sort_order: defaultOrder(slug), itemKeys: [] });
      bucket.get(slug)!.itemKeys.push(makeMergeKey(d));
    }
  }
  if (bucket.size === 0) {
    const featuredKeys = items.filter((x) => !!x.is_featured).map((x) => makeMergeKey(x));
    const trendingKeys = items.filter((x) => !!x.is_trending).map((x) => makeMergeKey(x));
    const lcKeys = items.filter((x) => x.source === "last_call").map((x) => makeMergeKey(x));
    if (featuredKeys.length) bucket.set("top_picks", { title: "Top Picks", subtitle: "Choix", sort_order: 2, itemKeys: featuredKeys });
    if (trendingKeys.length) bucket.set("hot_now", { title: "Hot Now", subtitle: "Tendance", sort_order: 1, itemKeys: trendingKeys });
    if (lcKeys.length) bucket.set("last_call", { title: "Last Call", subtitle: "Flux d’urgence", sort_order: 3, itemKeys: lcKeys });
  }
  return Array.from(bucket.entries()).map(([slug, cfg]) => ({ id: `view_${slug}`, slug, title: cfg.title, subtitle: cfg.subtitle, sort_order: cfg.sort_order, itemKeys: Array.from(new Set(cfg.itemKeys)), origin: "view" as const })).sort((a, b) => a.sort_order - b.sort_order || a.title.localeCompare(b.title, "fr"));
}

// -----------------------------
// COMPOSANT: LIVE COUNTDOWN
// -----------------------------
const LiveCountdown = ({ expiresAt }: { expiresAt: string }) => {
  const [timeLeft, setTimeLeft] = useState<string | null>(null);

  useEffect(() => {
    const target = new Date(expiresAt).getTime();
    if (!Number.isFinite(target)) return;

    const updateTime = () => {
      const now = Date.now();
      const diff = target - now;
      if (diff <= 0) { setTimeLeft("Expiré"); return; }
      const h = Math.floor(diff / (1000 * 60 * 60));
      const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const s = Math.floor((diff % (1000 * 60)) / 1000);
      if (h > 48) { setTimeLeft(`${h}h restantes`); } 
      else { setTimeLeft(`${String(h).padStart(2, '0')}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`); }
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  if (!timeLeft) return null;
  return (
    <div className="flex gap-1.5 items-center text-[10px] font-mono font-black text-slate-900 bg-white/95 px-2.5 py-1 rounded shadow-md backdrop-blur-sm border border-white/20">
      {timeLeft !== "Expiré" && <span className="text-red-600 animate-pulse">●</span>}
      <span>{timeLeft}</span>
    </div>
  );
};

// -----------------------------
// FETCHERS
// -----------------------------
async function fetchFindsFromBFF(): Promise<FindDeal[] | null> {
  if (!API_URL) return null;
  try {
    const res = await fetch(FINDS_ENDPOINT, { method: "GET", headers: { "Content-Type": "application/json" }, credentials: "include" });
    if (!res.ok) return null;
    const data = await res.json();
    const items: FindDeal[] = Array.isArray(data) ? data : (data?.items ?? []);
    if (!Array.isArray(items)) return [];
    return items.filter(Boolean).map((x: any) => ({ ...x, id: String(x.id), source: x.source === "last_call" ? "last_call" : "finds", source_id: String(x.source_id || x.id) })) as FindDeal[];
  } catch { return null; }
}
async function fetchFindsFromSupabaseViews(): Promise<FindDeal[]> {
  const candidates = ["forfeo_finds_public", "finds_public"];
  for (const tableName of candidates) {
    try {
      let query = supabase.from(tableName).select("*").limit(200);
      if (tableName === "forfeo_finds_public") query = query.order("sort_score", { ascending: false });
      const { data, error } = await query;
      if (!error && Array.isArray(data)) {
        return data.map((row: any) => {
          const mappedSource = String(row.source_type || row.source) === "last_call" ? "last_call" : "finds";
          return { ...row, id: `f_${row.id}`, source: mappedSource, source_id: String(row.source_id || row.id) } as FindDeal;
        });
      }
    } catch {}
  }
  return [];
}
async function fetchLastCallAsFinds(): Promise<FindDeal[]> {
  try {
    // Note: on retire le .gt("quantity_available", 0) pour gérer la Waitlist
    const { data, error } = await supabase.from("last_call_deals_public").select("*").gt("expires_at", new Date().toISOString()).limit(200);
    if (error || !Array.isArray(data)) return [];
    return data.map((r: any) => ({
      id: `lc_${String(r.id)}`, source: "last_call", source_id: String(r.id), title: r.title ?? "Last Call",
      description: r.description ?? null, original_price: r.original_price ?? null, price: r.discount_price ?? r.price ?? null, currency: "CAD",
      quantity_available: r.quantity_available ?? null, expires_at: r.expires_at ?? null, image_url: r.image_url ?? null, region: r.region_hint ?? null,
      type: "last_call", is_mystery: true, reveal_after_purchase: true, public_url: "/last-call", badge: "Last Call", badge_secondary: "Offre flash",
      collection_slugs: ["last_call", "hot_now"], lat: r.lat, lng: r.lng
    })) as FindDeal[];
  } catch { return []; }
}
async function fetchFindsOverrides(): Promise<any[]> {
  try {
    const { data, error } = await supabase.from("finds_overrides").select("*").eq("is_active", true).eq("is_hidden", false);
    return !error && Array.isArray(data) ? data : [];
  } catch { return []; }
}
function applyOverridesToItems(items: FindDeal[], overrides: any[]): FindDeal[] {
  if (!Array.isArray(overrides) || overrides.length === 0) return items;
  const map = new Map<string, any>();
  for (const o of overrides) map.set(`${o.source === "last_call" ? "last_call" : "finds"}:${String(o.source_id ?? "")}`, o);
  return items.map((item) => {
    const o = map.get(`${item.source}:${item.source_id}`);
    if (!o) return item;
    return { ...item, ...o, collection_slugs: coerceStringArray(o.collection_slugs) ?? item.collection_slugs, tags: coerceStringArray(o.tags) ?? item.tags } as FindDeal;
  }).filter((x) => !(x as any).is_hidden);
}
async function fetchFindsCollections(): Promise<FindCollection[]> {
  try {
    const { data: collections, error: cErr } = await supabase.from("finds_collections").select("id, slug, title, subtitle, sort_order").eq("is_active", true).order("sort_order", { ascending: true });
    if (cErr || !Array.isArray(collections) || collections.length === 0) return [];
    const ids = collections.map((c: any) => c.id);
    const { data: items, error: iErr } = await supabase.from("finds_collection_items").select("collection_id, source, source_id, sort_order, is_active").in("collection_id", ids).eq("is_active", true);
    if (iErr || !Array.isArray(items)) return collections.map((c: any) => ({ ...c, itemKeys: [], origin: "table" }));
    const grouped = new Map<string, string[]>();
    for (const it of items as any[]) {
      const key = `${it.source === "last_call" ? "last_call" : "finds"}:${it.source_id}`;
      const arr = grouped.get(String(it.collection_id)) || [];
      arr.push(key);
      grouped.set(String(it.collection_id), arr);
    }
    return collections.map((c: any) => ({ ...c, itemKeys: grouped.get(String(c.id)) || [], origin: "table" }));
  } catch { return []; }
}
function mergeUniqueById(listA: FindDeal[], listB: FindDeal[]) {
  const map = new Map<string, FindDeal>();
  [...listA, ...listB].forEach((x) => {
    if (!x) return;
    const key = makeMergeKey(x);
    if (!map.has(key)) return map.set(key, x);
    const cur = map.get(key)!;
    if (cur.source !== "last_call" && x.source === "last_call") return map.set(key, { ...cur, ...x });
    map.set(key, { ...cur, ...x });
  });
  return Array.from(map.values());
}

// -----------------------------
// SKELETON COMPONENT
// -----------------------------
const DealSkeleton = () => (
  <div className="bg-white rounded-3xl overflow-hidden shadow-sm border border-slate-100 flex flex-col h-[400px] animate-pulse">
    <div className="h-64 bg-slate-200 w-full"></div>
    <div className="p-6 flex-1 flex flex-col gap-4">
      <div className="h-4 bg-slate-200 rounded w-3/4"></div>
      <div className="h-4 bg-slate-200 rounded w-1/2"></div>
      <div className="mt-auto pt-4 border-t border-slate-50 flex justify-between items-end">
        <div className="h-8 bg-slate-200 rounded w-16"></div>
        <div className="h-10 bg-slate-200 rounded w-24"></div>
      </div>
    </div>
  </div>
);

// -----------------------------
// PAGE PRINCIPALE
// -----------------------------
export default function ForfeoFinds() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const mountedRef = useRef(true);
  
  const observerTarget = useRef<HTMLDivElement | null>(null);

  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [items, setItems] = useState<FindDeal[]>([]);
  const [collections, setCollections] = useState<FindCollection[]>([]);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  
  const [favoriteKeys, setFavoriteKeys] = useState<Set<string>>(new Set());
  const [seenKeys, setSeenKeys] = useState<Set<string>>(new Set());
  const [visibleCount, setVisibleCount] = useState(12);

  // Modal States
  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
  const [alertEmail, setAlertEmail] = useState("");
  const [alertSuccess, setAlertSuccess] = useState(false);

  const [waitlistDeal, setWaitlistDeal] = useState<FindDeal | null>(null);
  const [waitlistEmail, setWaitlistEmail] = useState("");
  const [waitlistSuccess, setWaitlistSuccess] = useState(false);

  const viewMode = searchParams.get("view") || "grid";
  const q = searchParams.get("q") || "";
  const region = searchParams.get("region") || "all";
  const type = searchParams.get("type") || "all";
  const budget = searchParams.get("budget") || "all";
  const windowFilter = searchParams.get("windowFilter") || "all";
  const sort = (searchParams.get("sort") as SortKey) || "hot";
  const selectedCollection = searchParams.get("collection") || "all";

  const onlyFeatured = searchParams.get("featured") === "true";
  const onlyTrending = searchParams.get("trending") === "true";
  const onlyLastCall = searchParams.get("lastcall") === "true";
  const onlyFavorites = searchParams.get("favorites") === "true";
  const hideSeen = searchParams.get("hideseen") === "true";

  const collectionBySlug = useMemo(() => {
    const map = new Map<string, FindCollection>();
    collections.forEach((c) => map.set(c.slug, c));
    return map;
  }, [collections]);

  const updateFilter = (key: string, value: string | boolean) => {
    setSearchParams(prev => {
      if (value === "all" || value === "" || value === false) prev.delete(key);
      else prev.set(key, String(value));
      return prev;
    });
    setVisibleCount(12);
  };

  useEffect(() => {
    try {
      const raw = localStorage.getItem("forfeo_finds_seen_keys");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setSeenKeys(new Set(parsed.slice(-100).map((x) => String(x))));
      }
    } catch {}
  }, []);

  useEffect(() => {
    try { localStorage.setItem("forfeo_finds_seen_keys", JSON.stringify(Array.from(seenKeys))); } catch {}
  }, [seenKeys]);

  const loadFavorites = async (userId: string | null) => {
    if (!userId) { setFavoriteKeys(new Set()); return; }
    try {
      const { data, error } = await supabase.from("finds_favorites").select("source, source_id").eq("user_id", userId);
      if (error || !Array.isArray(data)) return setFavoriteKeys(new Set());
      const next = new Set<string>();
      for (const r of data as any[]) next.add(`${r.source === "last_call" ? "last_call" : "finds"}:${String(r.source_id)}`);
      setFavoriteKeys(next);
    } catch { setFavoriteKeys(new Set()); }
  };

  const buildFindsDataset = async (): Promise<FindDeal[]> => {
    const fromBFF = await fetchFindsFromBFF();
    const fromViews = fromBFF === null ? await fetchFindsFromSupabaseViews() : fromBFF;
    const lastCall = await fetchLastCallAsFinds();
    const mergedBase = mergeUniqueById(fromViews || [], lastCall);
    const overrides = await fetchFindsOverrides();
    return applyOverridesToItems(mergedBase, overrides);
  };

  const loadCollectionsForItems = async (deals: FindDeal[]) => {
    const colsFromTables = await fetchFindsCollections();
    if (colsFromTables.length > 0) {
      if (!mountedRef.current) return;
      setCollections(colsFromTables);
      return;
    }
    const derived = buildCollectionsFromItems(deals);
    if (!mountedRef.current) return;
    setCollections(derived);
  };

  const hardRefresh = async (opts?: { reloadFavorites?: boolean; keepLoadingState?: boolean }) => {
    const keepLoadingState = !!opts?.keepLoadingState;
    if (!keepLoadingState) setLoading(true);
    try {
      const merged = await buildFindsDataset();
      if (!mountedRef.current) return;
      setItems(merged || []);
      await loadCollectionsForItems(merged || []);
      if (opts?.reloadFavorites && currentUserId) await loadFavorites(currentUserId);
    } finally {
      if (mountedRef.current && !keepLoadingState) setLoading(false);
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    if (!ENABLE_FINDS) { setLoading(false); setItems([]); setErrorMsg(null); return () => { mountedRef.current = false; }; }
    (async () => {
      setLoading(true); setErrorMsg(null);
      try {
        try {
          const authRes = await supabase.auth.getUser();
          const user = authRes?.data?.user;
          const uid = user?.id || null;
          if (mountedRef.current) {
            setCurrentUserId(uid);
            if (user?.email) {
              setAlertEmail(user.email);
              setWaitlistEmail(user.email);
            }
          }
          if (mountedRef.current) await loadFavorites(uid);
        } catch {}
        await hardRefresh({ keepLoadingState: true });
      } catch (e: any) {
        if (!mountedRef.current) return;
        setErrorMsg(e?.message || "Impossible de charger.");
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    })();

    const sub = supabase.channel("finds-live")
      .on("postgres_changes", { event: "*", schema: "public", table: "last_call_deals_public" }, () => { hardRefresh({ keepLoadingState: true }); })
      .on("postgres_changes", { event: "*", schema: "public", table: "finds_overrides" }, () => { hardRefresh({ keepLoadingState: true }); })
      .subscribe();
    return () => { mountedRef.current = false; supabase.removeChannel(sub); };
  }, []);

  // --- ACTIONS ALERTES & WAITLIST ---
  const handleSaveAlert = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!alertEmail) return;
    try {
      await supabase.from('finds_alerts').insert({
        user_id: currentUserId,
        email: alertEmail,
        region: region !== "all" ? region : null,
        category_type: type !== "all" ? type : null,
        budget: budget !== "all" ? budget : null,
        search_query: q || null
      });
      setAlertSuccess(true);
      setTimeout(() => { setIsAlertModalOpen(false); setAlertSuccess(false); }, 2000);
    } catch { alert("Erreur lors de la création de l'alerte."); }
  };

  const handleJoinWaitlist = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!waitlistEmail || !waitlistDeal) return;
    try {
      await supabase.from('finds_waitlist').insert({
        user_id: currentUserId,
        email: waitlistEmail,
        source: waitlistDeal.source,
        source_id: waitlistDeal.source_id
      });
      setWaitlistSuccess(true);
      setTimeout(() => { setWaitlistDeal(null); setWaitlistSuccess(false); }, 2000);
    } catch { alert("Tu es probablement déjà sur la liste d'attente pour cette offre."); }
  };

  // --- RECOMMANDATIONS "POUR VOUS" ---
  const recommendedDeals = useMemo(() => {
    if (!items || (favoriteKeys.size === 0 && seenKeys.size === 0)) return [];
    
    const activeKeys = new Set([...favoriteKeys, ...seenKeys]);
    const activeDeals = items.filter(d => activeKeys.has(makeMergeKey(d)));
    
    // On extrait les préférences
    const preferredTypes = new Set(activeDeals.map(d => String(d.type)).filter(Boolean));
    const preferredRegions = new Set(activeDeals.map(d => String(d.region)).filter(Boolean));

    const now = Date.now();
    
    // On trouve des offres non-vues qui matchent
    const recs = items.filter(d => {
      if (activeKeys.has(makeMergeKey(d))) return false;
      if (d.expires_at && new Date(d.expires_at).getTime() <= now) return false;
      if (d.quantity_available !== null && d.quantity_available !== undefined && safeNumber(d.quantity_available, 0) <= 0) return false;
      
      return preferredTypes.has(String(d.type)) || preferredRegions.has(String(d.region));
    });

    return recs.sort((a,b) => computeFindsScoreLocal(b) - computeFindsScoreLocal(a)).slice(0, 3);
  }, [items, favoriteKeys, seenKeys]);

  const regions = useMemo(() => {
    const set = new Set<string>();
    (Array.isArray(items) ? items : []).forEach(d => { const r = (d.region || "").trim(); if (r) set.add(r); });
    return ["all", ...Array.from(set).sort((a, b) => a.localeCompare(b, "fr"))];
  }, [items]);

  const types = useMemo(() => {
    const set = new Set<string>();
    (Array.isArray(items) ? items : []).forEach(d => { const t = (d.type || "").toString().trim(); if (t) set.add(t); });
    return ["all", ...Array.from(set).sort((a, b) => a.localeCompare(b, "fr"))];
  }, [items]);

  const filtered = useMemo(() => {
    const safeItems = Array.isArray(items) ? items : [];
    const now = Date.now();
    const qq = normalize(q.trim());
    const selectedCol = collectionBySlug.get(selectedCollection);
    const selectedKeys = selectedCol ? new Set(selectedCol.itemKeys) : null;

    return safeItems.filter((d) => {
      if (d.expires_at) { const exp = new Date(d.expires_at).getTime(); if (Number.isFinite(exp) && exp <= now) return false; }
      
      if (selectedCollection !== "all") {
        const inTableCollection = selectedKeys ? selectedKeys.has(makeMergeKey(d)) : false;
        const inViewCollection = (d.collection_slugs || []).includes(selectedCollection);
        if (!inTableCollection && !inViewCollection) return false;
      }

      if (onlyFeatured && !d.is_featured) return false;
      if (onlyTrending && !d.is_trending) return false;
      if (onlyLastCall && d.source !== "last_call") return false;
      if (onlyFavorites && !favoriteKeys.has(makeMergeKey(d))) return false;
      if (hideSeen && seenKeys.has(makeMergeKey(d))) return false;

      if (qq) {
        const blob = normalize(`${d.title ?? ""} ${d.description ?? ""} ${d.region ?? ""} ${d.type ?? ""} ${(d.tags || []).join(" ")}`);
        if (!blob.includes(qq)) return false;
      }
      if (region !== "all" && (d.region || "").trim() !== region) return false;
      if (type !== "all" && (d.type || "").toString().trim() !== type) return false;

      if (budget !== "all") {
        const p = safeNumber(d.price ?? null, NaN);
        if (Number.isFinite(p)) {
          if (budget === "0-50" && (p < 0 || p >= 50)) return false;
          if (budget === "50-100" && (p < 50 || p >= 100)) return false;
          if (budget === "100-200" && (p < 100 || p >= 200)) return false;
          if (budget === "200+" && p < 200) return false;
        }
      }

      if (windowFilter !== "all") {
        const exp = d.expires_at ? new Date(d.expires_at).getTime() : NaN;
        if (Number.isFinite(exp)) {
          const hours = (exp - now) / 3600000;
          if (hours <= 0) return false;
          if (windowFilter === "24h" && hours > 24) return false;
          if (windowFilter === "72h" && hours > 72) return false;
          if (windowFilter === "7d" && hours > 168) return false;
        }
      }
      return true;
    }).sort((a, b) => {
      // Les offres épuisées vont toujours à la fin
      const aSoldOut = a.quantity_available !== null && a.quantity_available !== undefined && safeNumber(a.quantity_available, 0) <= 0;
      const bSoldOut = b.quantity_available !== null && b.quantity_available !== undefined && safeNumber(b.quantity_available, 0) <= 0;
      if (aSoldOut && !bSoldOut) return 1;
      if (!aSoldOut && bSoldOut) return -1;

      const ap = safeNumber(a.price, 999999), bp = safeNumber(b.price, 999999);
      const ah = hoursLeft(a.expires_at), bh = hoursLeft(b.expires_at);
      if (sort === "price_asc") return ap - bp;
      if (sort === "price_desc") return bp - ap;
      if (sort === "expires") return (ah ?? 999999) - (bh ?? 999999);
      
      const aScore = computeFindsScoreLocal(a) + (a.source === "last_call" ? 5 : 0) + (a.sponsored ? Math.min(10, safeNumber(a.sponsor_priority, 0) / 10) : 0);
      const bScore = computeFindsScoreLocal(b) + (b.source === "last_call" ? 5 : 0) + (b.sponsored ? Math.min(10, safeNumber(b.sponsor_priority, 0) / 10) : 0);
      if (aScore !== bScore) return bScore - aScore;
      return (ah ?? 999999) - (bh ?? 999999);
    });
  }, [items, q, region, type, budget, windowFilter, sort, selectedCollection, collectionBySlug, onlyFeatured, onlyTrending, onlyFavorites, onlyLastCall, hideSeen, favoriteKeys, seenKeys]);

  const sections = useMemo(() => {
    // Dans les sections curées, on masque explicitement les offres épuisées pour garder l'accueil clean
    const all = filtered.filter(x => x.quantity_available == null || safeNumber(x.quantity_available, 0) > 0);
    return {
      all: filtered, // Le feed complet garde les épuisés à la fin
      featured: all.filter((x) => !!x.is_featured).slice(0, 6),
      trending: all.filter((x) => !!x.is_trending).slice(0, 6),
      expiringSoon: [...all].filter((x) => x.expires_at).sort((a, b) => new Date(a.expires_at || "").getTime() - new Date(b.expires_at || "").getTime()).slice(0, 6),
      lastCallOnly: all.filter((x) => x.source === "last_call").slice(0, 6),
      favoritesOnly: all.filter((x) => favoriteKeys.has(makeMergeKey(x))).slice(0, 6)
    };
  }, [filtered, favoriteKeys]);

  const showCuratedSections = selectedCollection === "all" && !q && region === "all" && type === "all" && budget === "all" && windowFilter === "all" && sort === "hot" && !onlyFeatured && !onlyTrending && !onlyFavorites && !onlyLastCall && !hideSeen;
  const visibleItems = showCuratedSections ? [] : filtered.slice(0, visibleCount);

  // --- INFINITE SCROLL OBSERVER ---
  useEffect(() => {
    if (visibleCount >= filtered.length || showCuratedSections || viewMode === "map") return;
    const currentObserver = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setVisibleCount((prev) => prev + 12);
      }
    }, { threshold: 0.1 });

    if (observerTarget.current) {
      currentObserver.observe(observerTarget.current);
    }
    return () => currentObserver.disconnect();
  }, [visibleCount, filtered.length, showCuratedSections, viewMode]);


  const handleToggleFavorite = async (e: React.MouseEvent, d: FindDeal) => {
    e.stopPropagation();
    if (!currentUserId) { navigate("/login"); return; }
    const key = makeMergeKey(d);
    const isAdding = !favoriteKeys.has(key);

    setFavoriteKeys(prev => { const next = new Set(prev); isAdding ? next.add(key) : next.delete(key); return next; });

    try {
      if (!isAdding) {
        await supabase.from("finds_favorites").delete().eq("user_id", currentUserId).eq("source", d.source).eq("source_id", d.source_id);
      } else {
        await supabase.from("finds_favorites").insert({ user_id: currentUserId, source: d.source, source_id: d.source_id });
      }
    } catch {
      setFavoriteKeys(prev => { const next = new Set(prev); isAdding ? next.delete(key) : next.add(key); return next; });
    }
  };

  const markSeen = (d: FindDeal) => {
    const key = makeMergeKey(d);
    setSeenKeys(prev => prev.has(key) ? prev : new Set(prev).add(key));
  };

  const handleShareClick = async (e: React.MouseEvent, d: FindDeal) => {
    e.stopPropagation();
    const shareUrl = `${window.location.origin}${d.public_url || "/last-call?src=" + d.source + "&id=" + d.source_id}`;
    if (navigator.share) {
      try { await navigator.share({ title: `Offre Forfeo : ${d.title}`, text: `Regarde cette trouvaille : ${d.title}`, url: shareUrl }); } catch {}
    } else {
      navigator.clipboard.writeText(shareUrl);
      alert("Lien copié !");
    }
  };

  const renderDealCard = (d: FindDeal, index: number, sectionId?: string) => {
    const badge = badgeForDeal(d);
    const disc = computeDiscountPct(d.original_price ?? null, d.price ?? null);
    const isFavorite = favoriteKeys.has(makeMergeKey(d));
    const isSeen = seenKeys.has(makeMergeKey(d));
    const isSoldOut = d.quantity_available !== null && d.quantity_available !== undefined && safeNumber(d.quantity_available, 0) <= 0;

    const displayTitle = d.teaser_title || (d.reveal_after_purchase || d.is_mystery ? (d.source === "last_call" ? d.title : (d.type ? `${String(d.type).toUpperCase()} • Offre mystère` : "Offre mystère")) : (d.title ?? "Offre"));
    const sub = d.teaser_subtitle || d.badge_secondary || (d.source === "last_call" ? "Offre Flash" : (d.reveal_after_purchase || d.is_mystery ? "Mystère" : (d.region ? `📍 ${d.region}` : "📍 Québec")));
    const badgeText = d.finds_badge ? `🏷️ ${d.finds_badge}` : d.badge ? `🏷️ ${d.badge}` : badge.text;

    return (
      <div key={`${d.id}_${index}`} className={`group bg-white rounded-3xl overflow-hidden shadow-lg border transition-all duration-300 flex flex-col h-full ${d.is_featured && !isSoldOut ? "border-amber-200" : "border-slate-100"} ${(isSeen || isSoldOut) ? "opacity-90" : ""} hover:-translate-y-1 ${isSoldOut ? "grayscale-[50%]" : ""}`}>
        <div className="h-64 relative bg-slate-900 overflow-hidden">
          {d.image_url ? (
            <img src={d.image_url} className={`w-full h-full object-cover transition duration-700 scale-105 ${d.reveal_after_purchase || d.is_mystery || isSoldOut ? "opacity-70 group-hover:opacity-50 blur-sm" : "opacity-90 group-hover:opacity-75"}`} alt="" loading="lazy" />
          ) : (<div className="w-full h-full flex items-center justify-center text-slate-300">Image à venir</div>)}
          
          <div className="absolute inset-0 flex flex-col justify-between p-4">
            <div className="flex justify-between items-start gap-2">
              <div className={`backdrop-blur-md text-[10px] font-black px-2 py-1 rounded flex items-center gap-1 border border-white/10 ${toneClass(d.finds_badge_tone ? toneFromOverride(d.finds_badge_tone) : badge.tone)} bg-opacity-80`}>{badgeText}</div>
              <div className="flex items-center gap-2">
                {disc !== null && !isSoldOut && <div className="bg-white text-slate-900 font-black text-xs px-2 py-1 rounded shadow-lg">-{disc}%</div>}
                <button onClick={(e) => handleShareClick(e, d)} className="bg-white/90 hover:bg-white text-slate-700 rounded-full w-8 h-8 flex items-center justify-center shadow-md transition">
                  <FaShareAlt size={12} />
                </button>
                <button onClick={(e) => handleToggleFavorite(e, d)} className="bg-white/90 hover:bg-white text-slate-900 rounded-full w-8 h-8 flex items-center justify-center shadow-md transition">
                  {isFavorite ? <FaHeart className="text-red-500" /> : <FaRegHeart className="text-slate-700" />}
                </button>
              </div>
            </div>
            <div className="self-center text-center px-2">
              <div className="text-white/70 font-bold uppercase text-[10px] tracking-widest mb-2 line-clamp-1">{sub}</div>
              <h3 className="text-2xl font-black text-white leading-none drop-shadow-xl line-clamp-2">{displayTitle}</h3>
            </div>
            <div className="flex justify-between items-end gap-2">
              {d.expires_at && !isSoldOut ? <LiveCountdown expiresAt={d.expires_at} /> : <div />}
              <div className="flex gap-1 flex-wrap justify-end">
                {d.is_featured && !isSoldOut && <span className="bg-amber-100 text-amber-900 text-[10px] font-black px-2 py-1 rounded-full">⭐ Choix</span>}
              </div>
            </div>
          </div>
        </div>
        <div className="p-6 flex-1 flex flex-col">
          {d.description && <p className="text-slate-600 text-sm mb-3 line-clamp-2">{clampText(d.description, 160)}</p>}
          <div className="flex items-end justify-between mt-auto pt-4 border-t border-slate-50 gap-3">
            <div className="min-w-0">
              {d.original_price ? <div className="text-slate-400 text-xs line-through font-bold">{formatMoney(d.original_price, d.currency ?? "CAD")}</div> : <div className="text-xs">&nbsp;</div>}
              <div className="text-3xl font-black text-slate-900 leading-none">{d.price !== null ? <>{safeNumber(d.price, 0)}<small className="text-sm align-top">$</small></> : "—"}</div>
            </div>
            
            {/* WAITLIST BUTTON FOR SOLD OUT */}
            {isSoldOut ? (
              <button
                onClick={() => setWaitlistDeal(d)}
                className="bg-slate-200 text-slate-700 pl-4 pr-4 py-3 rounded-xl hover:bg-slate-300 transition shadow-sm font-bold text-[10px] uppercase flex items-center gap-2"
              >
                <FaBell /> M'avertir
              </button>
            ) : (
              <button
                onClick={() => { markSeen(d); navigate(d.public_url || `/last-call?src=${d.source}&id=${d.source_id}`); }}
                className="bg-[#0B0F19] text-white pl-5 pr-4 py-3 rounded-xl hover:bg-blue-600 transition shadow-lg font-bold text-xs uppercase flex items-center gap-2 group-hover:scale-105"
              >
                {d.source === "last_call" ? <>Voir <FaBolt /></> : <>Découvrir</>}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderSection = (title: string, subtitle: string, deals: FindDeal[], sectionId: string, icon?: React.ReactNode) => {
    if (!deals.length) return null;
    return (
      <section className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <div><h2 className="text-xl font-black flex items-center gap-2">{icon}{title}</h2><p className="text-sm text-slate-500">{subtitle}</p></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {deals.map((d, i) => renderDealCard(d, i, sectionId))}
        </div>
      </section>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans">
      <Header />
      <main className="max-w-7xl mx-auto px-4 pt-28 pb-14">
        
        {/* HERO */}
        <section className="rounded-3xl border border-slate-200 bg-white shadow-sm overflow-hidden mb-6">
          <div className="p-6 md:p-8 bg-gradient-to-b from-slate-900 to-[#0B0F19] text-white">
            <h1 className="text-3xl md:text-5xl font-black tracking-tight mb-3">🔥 Trouvailles Forfeo</h1>
            <p className="text-slate-300">Explore nos meilleures offres locales et Last Call.</p>
          </div>
        </section>

        {/* COLLECTIONS */}
        {collections.length > 0 && (
          <section className="mb-6 rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Collections</div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => updateFilter("collection", "all")} className={`px-3 py-2 rounded-full text-xs font-black uppercase border transition ${selectedCollection === "all" ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}>Toutes</button>
              {collections.map((c) => (
                <button key={c.id} onClick={() => updateFilter("collection", c.slug)} className={`px-3 py-2 rounded-full text-xs font-black uppercase border transition ${selectedCollection === c.slug ? "bg-blue-600 text-white" : "bg-white text-slate-700 hover:bg-blue-50"}`}>{c.title}</button>
              ))}
            </div>
          </section>
        )}

        {/* FILTRES SYNCHRONISÉS */}
        <section className="bg-white p-4 rounded-3xl border border-slate-200 shadow-sm mb-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
            <div className="md:col-span-2">
              <input value={q} onChange={(e) => updateFilter("q", e.target.value)} className="w-full rounded-2xl border px-4 py-3 bg-slate-50 outline-none font-semibold text-slate-700 placeholder:text-slate-400" placeholder="Rechercher (Spa, resto...)" />
            </div>
            <select value={region} onChange={(e) => updateFilter("region", e.target.value)} className="w-full rounded-2xl border px-4 py-3 bg-slate-50 font-bold text-slate-700">
              {regions.map((r) => <option key={r} value={r}>{r === "all" ? "Toutes régions" : r}</option>)}
            </select>
            <select value={sort} onChange={(e) => updateFilter("sort", e.target.value)} className="w-full rounded-2xl border px-4 py-3 bg-slate-50 font-bold text-slate-700">
              <option value="hot">🔥 Pertinence</option>
              <option value="price_asc">💸 Prix ↑</option>
              <option value="price_desc">💸 Prix ↓</option>
            </select>
            <button onClick={() => { setSearchParams(new URLSearchParams()); setVisibleCount(12); }} className="w-full rounded-2xl border border-slate-200 bg-white text-xs font-black uppercase hover:bg-slate-50">Réinitialiser</button>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 pt-4 border-t border-slate-100">
            <div className="flex flex-wrap gap-2">
               <button onClick={() => updateFilter("featured", !onlyFeatured)} className={`px-3 py-1.5 text-xs font-bold rounded-full border ${onlyFeatured ? "bg-amber-100 border-amber-200 text-amber-900" : "bg-white text-slate-600 hover:bg-slate-50"}`}>⭐ Choix Forfeo</button>
               <button onClick={() => updateFilter("lastcall", !onlyLastCall)} className={`px-3 py-1.5 text-xs font-bold rounded-full border ${onlyLastCall ? "bg-red-100 border-red-200 text-red-900" : "bg-white text-slate-600 hover:bg-slate-50"}`}>⚡ Last Call</button>
               <button onClick={() => updateFilter("favorites", !onlyFavorites)} className={`px-3 py-1.5 text-xs font-bold rounded-full border ${onlyFavorites ? "bg-rose-100 border-rose-200 text-rose-900" : "bg-white text-slate-600 hover:bg-slate-50"}`}>❤️ Favoris</button>
               <button onClick={() => updateFilter("hideseen", !hideSeen)} className={`px-3 py-1.5 text-xs font-bold rounded-full border ${hideSeen ? "bg-slate-900 border-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>👀 Masquer vus</button>
            </div>
            
            {/* BOUTON ALERTES */}
            <button onClick={() => setIsAlertModalOpen(true)} className="text-xs font-black text-blue-600 hover:text-blue-800 flex items-center gap-2 bg-blue-50 px-3 py-1.5 rounded-full border border-blue-200">
               <FaBell /> Créer une alerte
            </button>
          </div>
        </section>

        {/* TOGGLE VUE GRILLE / CARTE */}
        <div className="flex justify-end mb-4">
          <div className="bg-slate-100 p-1 rounded-full flex gap-1 border border-slate-200 shadow-sm">
            <button onClick={() => updateFilter("view", "grid")} className={`px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest transition ${viewMode === "grid" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>Grille</button>
            <button onClick={() => updateFilter("view", "map")} className={`px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest transition ${viewMode === "map" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>Carte 📍</button>
          </div>
        </div>

        {/* FEED / GRILLE ET CARTE */}
        <section>
          {loading ? (
             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
               {[1,2,3,4,5,6].map(i => <DealSkeleton key={i} />)}
             </div>
          ) : filtered.length === 0 ? (
             <div className="text-center py-20 bg-white rounded-3xl border border-dashed border-slate-300">
                <div className="text-4xl mb-4">💤</div>
                <h3 className="text-xl font-bold text-slate-900">Aucune trouvaille ici...</h3>
                <button onClick={() => setIsAlertModalOpen(true)} className="mt-4 bg-blue-600 text-white px-6 py-2 rounded-full font-bold shadow-lg">M'avertir de nouveautés</button>
             </div>
          ) : viewMode === "map" ? (
             <DealsMap deals={filtered} onNavigate={(deal: any) => navigate(deal.public_url || `/last-call?src=${deal.source}&id=${deal.source_id}`)} />
          ) : (
             <>
               {showCuratedSections && (
                 <>
                   {renderSection("Recommandé pour vous", "Basé sur tes visites et favoris", recommendedDeals, "recommended", <FaHeart className="text-rose-500" />)}
                   {renderSection("Choix Forfeo", "Notre sélection", sections.featured, "featured", <FaStar className="text-amber-500" />)}
                   {renderSection("Tendance", "Ça bouge vite", sections.trending, "trending", <FaFire className="text-orange-500" />)}
                   {renderSection("Expire bientôt", "À ne pas manquer", sections.expiringSoon, "expiring", <FaClock className="text-amber-500" />)}
                   <div className="mt-8 border-t border-slate-200 pt-8"><h2 className="text-xl font-black mb-4">Toutes les offres</h2></div>
                 </>
               )}

               <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                 {(showCuratedSections ? sections.all : visibleItems).map((d, i) => renderDealCard(d, i, "all"))}
               </div>

               {/* INFINITE SCROLL TARGET */}
               {!showCuratedSections && visibleCount < filtered.length && (
                 <div ref={observerTarget} className="mt-10 flex justify-center py-8">
                   <div className="flex items-center gap-3 text-slate-400 font-bold text-sm">
                     <FaSpinner className="animate-spin text-blue-500 text-xl" />
                     Chargement de la suite...
                   </div>
                 </div>
               )}
             </>
          )}
        </section>

      </main>
      <Footer />

      {/* --- MODALE D'ALERTE --- */}
      {isAlertModalOpen && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl shadow-2xl p-6 w-full max-w-md relative animate-in fade-in zoom-in duration-200">
            <button onClick={() => setIsAlertModalOpen(false)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-900"><FaTimes /></button>
            <h3 className="text-xl font-black mb-2 flex items-center gap-2"><FaBell className="text-blue-600" /> Créer une alerte</h3>
            <p className="text-sm text-slate-600 mb-6">Reçois un email dès qu'une nouvelle offre correspond à tes filtres actuels.</p>
            
            <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 mb-6 text-xs text-slate-500 font-mono">
              Filtres : {region !== "all" ? region : "Toutes régions"} • {type !== "all" ? type : "Toutes catégories"} {q && `• "${q}"`}
            </div>

            {alertSuccess ? (
              <div className="bg-emerald-100 text-emerald-900 p-4 rounded-xl font-bold text-center">Alerte enregistrée avec succès !</div>
            ) : (
              <form onSubmit={handleSaveAlert} className="flex flex-col gap-4">
                <input 
                  type="email" 
                  required 
                  value={alertEmail} 
                  onChange={(e) => setAlertEmail(e.target.value)} 
                  placeholder="Ton adresse courriel" 
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-blue-500"
                />
                <button type="submit" className="w-full bg-slate-900 text-white font-bold py-3 rounded-xl shadow-lg hover:bg-blue-600 transition">Confirmer l'alerte</button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* --- MODALE WAITLIST --- */}
      {waitlistDeal && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl shadow-2xl p-6 w-full max-w-md relative animate-in fade-in zoom-in duration-200">
            <button onClick={() => setWaitlistDeal(null)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-900"><FaTimes /></button>
            <h3 className="text-xl font-black mb-2 flex items-center gap-2">Liste d'attente</h3>
            <p className="text-sm text-slate-600 mb-6">Cette offre est actuellement épuisée. Laisse ton courriel pour être le premier averti si une place se libère.</p>
            
            <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 mb-6 flex items-center gap-3">
              {waitlistDeal.image_url && <img src={waitlistDeal.image_url} className="w-12 h-12 object-cover rounded-lg" alt="" />}
              <div className="text-xs font-bold text-slate-900 line-clamp-2">{waitlistDeal.title}</div>
            </div>

            {waitlistSuccess ? (
              <div className="bg-emerald-100 text-emerald-900 p-4 rounded-xl font-bold text-center">Tu es sur la liste d'attente !</div>
            ) : (
              <form onSubmit={handleJoinWaitlist} className="flex flex-col gap-4">
                <input 
                  type="email" 
                  required 
                  value={waitlistEmail} 
                  onChange={(e) => setWaitlistEmail(e.target.value)} 
                  placeholder="Ton adresse courriel" 
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-blue-500"
                />
                <button type="submit" className="w-full bg-slate-900 text-white font-bold py-3 rounded-xl shadow-lg hover:bg-slate-700 transition">Rejoindre la liste</button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
